import {
  GlobalDesignPanelProps,
  HostMetadata,
  Styles,
  StylesDerivedNativeActionNames,
} from '../../types';
import { CompRef } from '@wix/document-services-types';
import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';
import { EditorElement } from '@wix/component-protocol/schema';
import { filterStylesByActionName } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/stylesFilter';
import { Host } from '@wix/sdk';
import type { EditorPointer } from '@wix/editor-document-logic-api';

type PointerContexts = Map<EditorPointer<CompRef>, Context>;

type Context = { styles: Styles; presets: EditorElement['presets'] };

const processElement = (
  actionName: StylesDerivedNativeActionNames,
  editorPointer: EditorPointer<CompRef>,
  context: VirtualManifestAPI,
  pointerContexts: PointerContexts,
  metadataMap: Map<EditorPointer<CompRef>, HostMetadata>,
) => {
  const styles = context.resolver.getStyleDefinitions();
  const presets = context.resolver.getPresetDefinitions();
  const rootDisplayName = context.resolver.getDisplayName();
  const extensionType = context.resolver.getExtensionType();

  const allowedStyles = filterStylesByActionName(actionName, styles);

  metadataMap.set(editorPointer, {
    rootDisplayName,
    extensionType,
    editorPointerId: editorPointer.asUniqueKey,
  });
  pointerContexts.set(editorPointer, { styles: allowedStyles, presets });

  const childrenContexts = context.getChildren();

  if (childrenContexts) {
    for (const childContext of Object.values(childrenContexts)) {
      const childPointer: EditorPointer<CompRef> | null =
        childContext.getEditorPointer();

      if (!childPointer) {
        return;
      }

      processElement(
        actionName,
        childPointer,
        childContext,
        pointerContexts,
        metadataMap,
      );
    }
  }
};

const createHost = async (
  editorPointer: EditorPointer<CompRef>,
  context: Context,
  getHost: GlobalDesignPanelProps['getHost'],
) => {
  const host = await getHost({
    contextBuilder: (builder) => {
      const element: EditorElement = {
        ...context.styles,
        presets: context.presets,
      };

      builder.withEditorElement(editorPointer, element);
    },
  });

  return host;
};

export const getHosts = async (
  actionName: StylesDerivedNativeActionNames,
  rootEditorPointers: EditorPointer<CompRef>[],
  getHost: GlobalDesignPanelProps['getHost'],
  componentsAPI: GlobalDesignPanelProps['componentsAPI'],
): Promise<{ host: Host; metadata: HostMetadata }[]> => {
  const pointerContexts: PointerContexts = new Map();
  const metadataMap: Map<EditorPointer<CompRef>, HostMetadata> = new Map();

  rootEditorPointers.forEach((editorPointer) => {
    const context = componentsAPI.buildManifestAPI({
      editorPointers: [editorPointer],
    });

    processElement(
      actionName,
      editorPointer,
      context,
      pointerContexts,
      metadataMap,
    );
  });

  const hostCreationPromises = Array.from(
    pointerContexts.entries(),
    async ([editorPointer, styleContext]) => {
      const host = await createHost(editorPointer, styleContext, getHost);
      const metadata = metadataMap.get(editorPointer) || {
        rootDisplayName: '',
        extensionType: '',
        editorPointerId: '',
      };
      return { host, metadata };
    },
  );

  return Promise.all(hostCreationPromises);
};
